import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const modelsEndpoint = ({ baseUrl }: Record<string, string>) =>
  `${(baseUrl ?? "").replace(/\/$/, "")}/models`;

export const openaiCompatible = defineConnector({
  identifier: "openaiCompatible",
  name: "OpenAI compatible",
  tagLine: "Connect to any OpenAI-compatible API endpoint",
  logo,
  protocol: "http",
  tags: ["ai", "engineering", "custom"],
  keywords:
    "openai-compatible openai compatible vllm litellm llm inference endpoint self-hosted",
  // No default host: the key must never be broadcast to api.openai.com, so the
  // credential is scoped to the user's own base URL.
  defaultUrlPattern: ({ baseUrl }) => domainToRegex(baseUrl),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Enter your endpoint details",
      items: [
        {
          kind: "url",
          label: "Base URL",
          placeholder: "https://llm.example.com/v1",
          required: true,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Your provider's OpenAI-compatible API base URL.",
        },
        {
          kind: "input",
          label: "API key",
          placeholder: "sk-…",
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "chat-model" }],
          addLabel: "Add model",
          fetch: openAiModelsFetch(
            "Fetch models from endpoint",
            modelsEndpoint
          ),
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: modelsEndpoint,
  },
  publicEnvVars: ({ baseUrl }) => ({
    OPENAI_BASE_URL: baseUrl || "",
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.OPENAI_BASE_URL as the OpenAI-compatible API base URL. Pass a placeholder API key; the proxy injects the real key from the connector.",
      configuration
    ),
  usage: openaiUsage(),
});
